/**
 * Session recovery that keeps the user's work (F19).
 *
 * Before: a write that hit an expired session showed "Session expired. Please log
 * in again." and, when the silent refresh also failed, the api-client wiped the
 * persisted auth state and navigated to /login, taking any unsaved draft with it.
 *
 * Now the api-client asks this module instead. A write that cannot be replayed on
 * its own waits here, pending, while <SessionRecoveryDialog> tells the user what
 * happened and lets them sign in again in a new tab. When they choose Retry, every
 * waiting request is sent once more with the renewed cookies; Cancel rejects them.
 * Nothing on the page is navigated away or cleared, so the draft stays where it was.
 *
 * Framework-free (a tiny observable) so the api-client can use it without React.
 */

export interface SessionRecoveryState {
  open: boolean;
  /** true: the session was renewed silently; the user only has to confirm the resend. */
  renewed: boolean;
  /** Writes waiting for the user's decision. */
  waiting: number;
}

type Waiter = { resolve: () => void; reject: (e: unknown) => void };

let state: SessionRecoveryState = { open: false, renewed: false, waiting: 0 };
let waiters: Waiter[] = [];
const listeners = new Set<(s: SessionRecoveryState) => void>();

function emit(next: Partial<SessionRecoveryState>) {
  state = { ...state, ...next };
  listeners.forEach(l => l(state));
}

export function getSessionRecoveryState(): SessionRecoveryState {
  return state;
}

export function subscribeSessionRecovery(listener: (s: SessionRecoveryState) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Hold a request until the user decides. Resolves on Retry, rejects on Cancel.
 * `renewed` is sticky-false: if any waiter needs a real sign-in, the dialog says so.
 */
export function awaitSessionRecovery(opts: { renewed: boolean }): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    waiters.push({ resolve, reject });
    emit({
      open: true,
      renewed: state.open ? state.renewed && opts.renewed : opts.renewed,
      waiting: waiters.length,
    });
  });
}

/** A read failed for the same reason: show the prompt, nothing to hold. */
export function announceSessionExpired(): void {
  if (!state.open) emit({ open: true, renewed: false, waiting: waiters.length });
}

/** The user chose Retry: release every held request. */
export function retrySessionRecovery(): void {
  const release = waiters;
  waiters = [];
  emit({ open: false, renewed: false, waiting: 0 });
  release.forEach(w => w.resolve());
}

/** The user chose Cancel: fail every held request with the original error. */
export function cancelSessionRecovery(reason?: unknown): void {
  const release = waiters;
  waiters = [];
  emit({ open: false, renewed: false, waiting: 0 });
  release.forEach(w => w.reject(reason ?? new Error('Cancelled after the session expired.')));
}

/**
 * A sign-in in another tab is exactly steps 1–2 of the dialog, so finish step 3
 * for the user: close the prompt and resend what was waiting. Without this, a
 * second tab's sign-in (which briefly ends the shared session) left this tab
 * blocked behind "Your session expired" although the new session was fine.
 */
function signedInElsewhere(e: StorageEvent): boolean {
  if (e.key !== 'auth-storage' || !e.newValue) return false;
  try {
    const next = JSON.parse(e.newValue)?.state;
    const prev = e.oldValue ? JSON.parse(e.oldValue)?.state : null;
    return !!next?.isAuthenticated && !!next?.csrfToken && next.csrfToken !== prev?.csrfToken;
  } catch { return false; }
}
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    // After the auth store's own storage listener has rehydrated the new token.
    if (state.open && signedInElsewhere(e)) setTimeout(retrySessionRecovery, 0);
  });
}

/** Test helper. */
export function __resetSessionRecoveryForTests(): void {
  waiters = [];
  state = { open: false, renewed: false, waiting: 0 };
  listeners.clear();
}
